import { Component, Inject, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';

import {
  AngularYandexMapsModule,
  YaMapComponent,
  YaReadyEvent,
} from 'angular8-yandex-maps';

import { NavbarComponent } from '../navbar/navbar.component';
import { FaqComponent } from '../faq/faq.component';
import { ContactsComponent } from '../contacts/contacts.component';
import { FooterComponent } from '../footer/footer.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { DragNDropComponent } from '../drag-n-drop/drag-n-drop.component';
import { QuillEditorComponent } from 'ngx-quill';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { PaymentService } from '../../services/payment.service';
import { ManagementService } from '../../services/management.service';
import { ObjectManagement } from '../../models/object-management';
import { HttpErrorResponse } from '@angular/common/http';
import { NotificationService } from '../../services/notification.service';
import { ObjectTable } from '../../models/object-table';
import { ObjectService } from '../../services/object.service';
import { environment } from '../../../environments/environment';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { firstValueFrom } from 'rxjs';
import { WINDOW } from '../../window';
import { PLATFORM_ID } from '@angular/core';

@Component({
  selector: 'app-client-order',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule,
    AngularYandexMapsModule,
    NavbarComponent,
    FaqComponent,
    ContactsComponent,
    FooterComponent,
    DragNDropComponent,
    QuillEditorComponent,
  ],
  templateUrl: './client-order.component.html',
  styleUrl: './client-order.component.scss',
})
export class ClientOrderComponent implements OnInit {
  private recaptchaV3 = inject(ReCaptchaV3Service);
  captchaSiteKey = environment.captchaSiteKey;
  captchaToken = '';

  private readonly isBrowser: boolean;

  readonly coordsValidator: ValidatorFn = (
    control: AbstractControl
  ): ValidationErrors | null => {
    const value = (control.value ?? '').trim();
    if (!value) {
      return null;
    }

    const parts = value.split(',');
    if (parts.length !== 2) {
      return { coords: true };
    }

    const lat = +parts[0];
    const lng = +parts[1];

    const ok =
      !Number.isNaN(lat) &&
      !Number.isNaN(lng) &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180;

    return ok ? null : { coords: true };
  };

  form: FormGroup = this.fb.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    patronymic: [''],
    birthDate: [''],
    deathDate: [''],
    burialPlace: ['', [this.coordsValidator]],
    files: [<File[]>[]],
    descriptionHtml: [''],
    customerFirstName: ['', Validators.required],
    customerLastName: ['', Validators.required],
    customerPatronymic: [''],
    phoneNumber: ['', Validators.required],
    agreement: [false, Validators.requiredTrue],
    termsOfOffer: [false, Validators.requiredTrue],
    captcha: ['', Validators.required],
  });

  /* ya-map */
  showMap = false;
  mapCenter = [53.90374568290297, 27.558605069575954];
  zoom = 9;
  @ViewChild(YaMapComponent) mapComponent!: YaMapComponent;
  private mapInstance!: ymaps.Map;
  private placemark?: ymaps.Placemark;
  private typedCoords: number[] | null = null;

  constructor(
    private fb: FormBuilder,
    private paymentService: PaymentService,
    private managementService: ManagementService,
    public notification: NotificationService,
    private objectService: ObjectService,
    @Inject(WINDOW) private win: Window,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    this.form.get('burialPlace')!.valueChanges.subscribe((val: string) => {
      this.onCoordsTyped(val);
    });
  }

  ngOnInit(): void {
    if (this.isBrowser && typeof this.win?.scrollTo === 'function') {
      this.win.scrollTo(0, 0);
    }
  }

  private fetchCaptcha(): Promise<string> {
    return firstValueFrom(this.recaptchaV3.execute('create_request'));
  }

  toggleMap() {
    this.showMap = !this.showMap;
    if (!this.isBrowser) return;

    if (this.showMap && this.mapInstance && this.typedCoords) {
      this.drawPlacemark(this.typedCoords);
    }
  }

  onMapReady(e: YaReadyEvent) {
    if (!this.isBrowser) return;

    this.mapInstance = e.target;
    this.mapInstance.events.add('click', (evt: any) => this.onMapClick(evt));
    if (this.typedCoords) {
      this.drawPlacemark(this.typedCoords);
    }
  }

  private onMapClick(event: any) {
    if (!this.isBrowser) return;

    const coords: number[] = event.get('coords');
    this.form.patchValue({ burialPlace: coords.join(', ') });
  }

  private onCoordsTyped(raw: string) {
    const coords = this.parseCoords(raw);
    this.typedCoords = coords;
    if (!this.isBrowser) return;

    if (coords && this.mapInstance) {
      this.drawPlacemark(coords);
    }
  }

  private drawPlacemark(coords: number[]) {
    if (!this.isBrowser) return;
    if (!this.mapInstance) return;

    const ym = (this.win as any)?.ymaps;
    if (!ym) return;

    if (!this.placemark) {
      this.placemark = new ym.Placemark(
        coords,
        { hintContent: 'Место погребения' },
        { draggable: true }
      );
      this.mapInstance.geoObjects.add(this.placemark);
    } else {
      this.placemark.geometry!.setCoordinates(coords);
    }

    this.placemark.events.add('dragend', () => {
      const newCoords = this.placemark!.geometry!.getCoordinates();
      this.form.patchValue({ burialPlace: newCoords.join(', ') });
    });

    this.mapInstance.setCenter(coords);
  }

  private parseCoords(raw: string): number[] | null {
    if (!raw) return null;
    const parts = raw.split(',');
    if (parts.length !== 2) return null;
    const lat = +parts[0];
    const lng = +parts[1];

    return !Number.isNaN(lat) &&
      !Number.isNaN(lng) &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180
      ? [lat, lng]
      : null;
  }

  onFilesAdded(files: any) {
    this.form.patchValue({ files });
    this.form.get('files')!.setValue(files);
    this.form.get('files')!.markAsDirty();
  }

  async onCreateConfirm(): Promise<void> {
    this.form.markAllAsTouched();

    try {
      this.captchaToken = await this.fetchCaptcha();
    } catch {
      this.notification.show(
        'Не удалось выполнить проверку reCAPTCHA',
        'error'
      );
      return;
    }

    this.form.patchValue({ captcha: this.captchaToken });

    if (this.form.valid) {
      let objectFormValues = { ...this.form.value };
      const objectContext: ObjectTable[] =
        this.createObjectPageContext(objectFormValues);
      const requestObject = {
        agreement: objectFormValues.agreement,
        birthDate: objectFormValues.birthDate,
        burialPlace: objectFormValues.burialPlace,
        customerFirstName: objectFormValues.customerFirstName,
        customerLastName: objectFormValues.customerLastName,
        customerPatronymic: objectFormValues.customerPatronymic,
        deathDate: objectFormValues.deathDate,
        descriptionHtml: null,
        firstName: objectFormValues.firstName,
        lastName: objectFormValues.lastName,
        patronymic: objectFormValues.patronymic,
        phoneNumber: objectFormValues.phoneNumber,
        context: objectContext,
        captchaToken: this.captchaToken,
      };
      this.managementService.createRequest(requestObject, 'ru').subscribe(
        (object: ObjectManagement) => {
          this.uploadFiles(objectFormValues, object);
          this.openPaymentLink(object.uuid);
        },
        (error: HttpErrorResponse) => {
          const detail: string =
            typeof error.error === 'object' && error.error?.detail
              ? String(error.error.detail)
              : '';

          if (
            detail
              .toLowerCase()
              .startsWith('unpublished objects limit exceeded')
          ) {
            this.notification.show(
              'К сожалению, в данный момент мы не можем обработать ваш запрос. ' +
                'Попробуйте позже или свяжитесь с нами по контактным данным:\n\n' +
                'Тел.: +375 44 711 55 11\n' +
                'Viber: +375 44 569 9547\n' +
                'Telegram: +375 44 569 9547',
              'error'
            );
          } else {
            this.notification.show(
              'Произошла ошибка при создании объекта',
              'error'
            );
          }
        }
      );
    }
  }

  private createObjectPageContext(objectFormValues: any): ObjectTable[] {
    const formattedTables: ObjectTable[] = [
      {
        tableName: 'Объект',
        columns: [
          {
            header: 'Имя, название',
            data: [
              `${objectFormValues.lastName} ${objectFormValues.firstName} ${objectFormValues.patronymic}`,
            ],
          },
          {
            header: 'Дата 1',
            data: [objectFormValues.birthDate?.toLocaleDateString?.() ?? ''],
          },
          {
            header: 'Дата 2',
            data: [objectFormValues.deathDate?.toLocaleDateString?.() ?? ''],
          },
        ],
      },
      {
        tableName: 'Подробно',
        columns: [
          {
            header: 'Заголовок',
            data: [null],
          },
          {
            header: 'Описание',
            data: [objectFormValues.descriptionHtml],
          },
        ],
      },
      {
        tableName: 'Ссылки',
        columns: [
          {
            header: 'Заголовок',
            data: [null],
          },
          {
            header: 'Наименование',
            data: [null],
          },
          {
            header: 'Ресурс',
            data: [null],
          },
          {
            header: 'Описание',
            data: [null],
          },
        ],
      },
      {
        tableName: 'Расположение',
        columns: [
          {
            header: 'Заголовок',
            data: ['Координаты:'],
          },
          {
            header: 'Описание',
            data: [objectFormValues.burialPlace],
          },
        ],
      },
    ];
    return formattedTables;
  }

  private uploadFiles(objectFormValues: any, object: ObjectManagement) {
    for (let file of objectFormValues.files) {
      this.managementService.uploadRequestFiles(file, object.uuid).subscribe(
        () => {},
        () => {
          this.notification.show(
            `Произошла ошибка при загрузке файла ${file}`,
            'error'
          );
        }
      );
    }
  }

  openPaymentLink(uuid: string) {
    this.paymentService.getPaymentLink(uuid, 20000).subscribe(
      (link: string) => {
        if (this.isBrowser && typeof this.win?.open === 'function') {
          this.win.open(link, '_blank', 'noopener');
        }
      },
      (error: HttpErrorResponse) => {
        this.notification.show(
          'Произошла ошибка при генерации ссылки на оплату',
          'error'
        );
      }
    );
  }
}
